import React from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { LogOut, LayoutDashboard, Newspaper, Mail, Image, Settings, DollarSign, Users, Handshake, Bell, Share2 } from 'lucide-react';

const AdminLayout = () => {
  const { isAuthenticated, loading, logout } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  const navItems = [
    { path: '/admin/dashboard', label: t('admin.dashboard'), icon: LayoutDashboard },
    { path: '/admin/news', label: t('admin.news_management'), icon: Newspaper },
    { path: '/admin/contacts', label: t('admin.contacts'), icon: Mail },
    { path: '/admin/team', label: 'Team', icon: Users },
    { path: '/admin/partners', label: 'Partner', icon: Handshake },
    { path: '/admin/info-alerts', label: 'Info-Meldungen', icon: Bell },
    { path: '/admin/prices', label: 'Preislisten', icon: DollarSign },
    { path: '/admin/images', label: t('admin.images'), icon: Image },
    { path: '/admin/social-media', label: 'Social Media', icon: Share2 },
    { path: '/admin/settings', label: t('admin.settings'), icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-layout">
      {/* Top Navigation */}
      <nav className="bg-blue-600 text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold">Surfmotion Admin</h1>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center space-x-2 px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('admin.logout')}</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white shadow-md min-h-[calc(100vh-4rem)]">
          <nav className="p-4" data-testid="admin-sidebar">
            <ul className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`admin-nav-${item.path.split('/').pop()}`}
                      className={`flex items-center space-x-3 px-4 py-2 rounded-md transition-colors ${
                        isActive
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;